import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAppDispatch } from '@/hooks'
import storeService, { AdminStore, StoreProfilePayload } from '@/services/store-service'
import { setCurrentStore } from '@/slices/store'

const DAYS = [
	{ key: 'mon', label: 'Montag' },
	{ key: 'tue', label: 'Dienstag' },
	{ key: 'wed', label: 'Mittwoch' },
	{ key: 'thu', label: 'Donnerstag' },
	{ key: 'fri', label: 'Freitag' },
	{ key: 'sat', label: 'Samstag' },
	{ key: 'sun', label: 'Sonntag' },
] as const

type HoursForm = Record<string, string>

const hoursToForm = (hours: AdminStore['working_hours']): HoursForm => {
	const out: HoursForm = {}
	for (const d of DAYS) {
		const slots = (hours as any)?.[d.key] as Array<{ start: string; end: string }> | undefined
		out[d.key] = slots?.length ? slots.map(s => `${s.start}-${s.end}`).join(', ') : ''
	}
	return out
}

const formToHours = (form: HoursForm) => {
	const out: Record<string, Array<{ start: string; end: string }>> = {}
	for (const d of DAYS) {
		const raw = (form[d.key] || '').trim()
		if (!raw) {
			out[d.key] = []
			continue
		}
		out[d.key] = raw.split(',').map(part => {
			const [start, end] = part.trim().split('-').map(s => s.trim())
			return { start: start || '', end: end || '' }
		}).filter(s => s.start && s.end)
	}
	return out
}

export const StoreProfileEditor = () => {
	const dispatch = useAppDispatch()
	const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
	const [form, setForm] = useState<StoreProfilePayload>({})
	const [hoursForm, setHoursForm] = useState<HoursForm>(hoursToForm(null))

	const { data: stores = [], isLoading, refetch } = useQuery(
		'store-admin-my-stores',
		() => storeService.listMyStores(),
		{
			refetchOnWindowFocus: false,
			onError: () => toast.error('Stores konnten nicht geladen werden'),
			onSuccess: list => {
				if (!selectedUuid && list[0]) setSelectedUuid(list[0].uuid)
			},
		},
	)

	const selected = stores.find(s => s.uuid === selectedUuid) || stores[0]

	useEffect(() => {
		if (!selected) return
		setSelectedUuid(selected.uuid)
		setForm({
			tagline: selected.tagline || '',
			description: selected.description || '',
			logo_url: selected.logo_url || '',
			cover_url: selected.cover_url || '',
			address: selected.address || '',
			city: selected.city || '',
			zip: selected.zip || '',
			country: selected.country || 'DE',
			phone: selected.phone || '',
			email: selected.email || '',
			instagram: selected.instagram || '',
			invoice_counter: selected.invoice_counter ?? 0,
			geocode: true,
		})
		setHoursForm(hoursToForm(selected.working_hours as any))
		dispatch(
			setCurrentStore({
				uuid: selected.uuid,
				slug: selected.slug,
				name: selected.name,
				logo_url: selected.logo_url,
				tagline: selected.tagline,
				city: selected.city,
				zip: selected.zip,
				address: selected.address,
				phone: selected.phone,
				email: selected.email,
				instagram: selected.instagram,
				working_hours: selected.working_hours as any,
				invoice_counter: selected.invoice_counter ?? 0,
			}),
		)
	}, [selected?.uuid, dispatch])

	const save = useMutation(
		() =>
			storeService.updateMyStoreProfile(selected!.uuid, {
				...form,
				working_hours: formToHours(hoursForm),
			}),
		{
			onSuccess: () => {
				toast.success('Profil gespeichert')
				const nextCounter = Number(form.invoice_counter) || 0
				if (selected) {
					dispatch(
						setCurrentStore({
							uuid: selected.uuid,
							slug: selected.slug,
							name: selected.name,
							logo_url: selected.logo_url,
							tagline: form.tagline || selected.tagline,
							city: form.city || selected.city,
							zip: form.zip || selected.zip,
							address: form.address || selected.address,
							phone: form.phone || selected.phone,
							email: form.email || selected.email,
							instagram: form.instagram || selected.instagram,
							working_hours: formToHours(hoursForm),
							invoice_counter: nextCounter,
						}),
					)
				}
				refetch()
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Speichern fehlgeschlagen')
			},
		},
	)

	if (isLoading) {
		return (
			<div className="flex justify-center py-16">
				<Loader2 className="animate-spin" />
			</div>
		)
	}

	if (!selected) {
		return (
			<div className="rounded-[20px] bg-white p-6 text-muted-foreground text-sm">
				Kein Store für dieses Konto hinterlegt. Bitte Super Admin kontaktieren.
			</div>
		)
	}

	const set = (key: keyof StoreProfilePayload, value: string) =>
		setForm(prev => ({ ...prev, [key]: value }))

	return (
		<div className="rounded-[20px] bg-white p-4 lg:p-6 flex flex-col gap-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-secondary font-extrabold text-lg">Store-Profil</h2>
					<p className="text-muted-foreground text-sm">
						Logo, Texte, Öffnungszeiten und Kontakt — Preise nur über Super Admin
					</p>
				</div>
				{stores.length > 1 && (
					<select
						className="border rounded-xl h-10 px-3 text-sm"
						value={selected.uuid}
						onChange={e => setSelectedUuid(e.target.value)}>
						{stores.map(s => (
							<option key={s.uuid} value={s.uuid}>
								{s.name}
							</option>
						))}
					</select>
				)}
			</div>

			<div className="grid sm:grid-cols-2 gap-3">
				<Field label="Tagline" value={form.tagline || ''} onChange={v => set('tagline', v)} />
				<Field label="Logo URL" value={form.logo_url || ''} onChange={v => set('logo_url', v)} />
				<Field label="Cover URL" value={form.cover_url || ''} onChange={v => set('cover_url', v)} />
				<Field label="Telefon" value={form.phone || ''} onChange={v => set('phone', v)} />
				<Field label="E-Mail" value={form.email || ''} onChange={v => set('email', v)} />
				<Field
					label="Instagram"
					value={form.instagram || ''}
					onChange={v => set('instagram', v)}
				/>
				<Field label="Adresse" value={form.address || ''} onChange={v => set('address', v)} />
				<Field label="PLZ" value={form.zip || ''} onChange={v => set('zip', v)} />
				<Field label="Stadt" value={form.city || ''} onChange={v => set('city', v)} />
				<Field label="Land" value={form.country || 'DE'} onChange={v => set('country', v)} />
				<Field
					label="Rechnungsnummer (Zähler)"
					value={String(form.invoice_counter ?? 0)}
					onChange={v => set('invoice_counter', v)}
				/>
			</div>

			<div>
				<Label className="text-xs">Beschreibung</Label>
				<Textarea
					className="mt-1 min-h-28"
					value={form.description || ''}
					onChange={e => set('description', e.target.value)}
				/>
			</div>

			<div>
				<p className="text-sm font-semibold text-secondary mb-2">
					Öffnungszeiten (z.B. 10:00-13:00, 14:00-18:00)
				</p>
				<div className="grid sm:grid-cols-2 gap-3">
					{DAYS.map(d => (
						<div key={d.key}>
							<Label className="text-xs">{d.label}</Label>
							<Input
								className="mt-1"
								placeholder="leer = geschlossen"
								value={hoursForm[d.key] || ''}
								onChange={e =>
									setHoursForm(prev => ({ ...prev, [d.key]: e.target.value }))
								}
							/>
						</div>
					))}
				</div>
			</div>

			<Button type="button" disabled={save.isLoading} onClick={() => save.mutate()}>
				{save.isLoading && <Loader2 className="animate-spin" />}
				Profil speichern
			</Button>
		</div>
	)
}

const Field = ({
	label,
	value,
	onChange,
}: {
	label: string
	value: string
	onChange: (v: string) => void
}) => (
	<div>
		<Label className="text-xs">{label}</Label>
		<Input className="mt-1" value={value} onChange={e => onChange(e.target.value)} />
	</div>
)
